import React, { useEffect, useState, useRef } from 'react';
import api from '../../api/client';
import { MessageSquare, Trash2, Loader2, Send, User as UserIcon, Bot, Shield } from 'lucide-react';

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString('az-AZ'); } catch { return iso; }
}

function Msg({ m }) {
  const isUser = m.role === 'user';
  const isAdmin = m.role === 'admin';
  const bg = isUser ? '#17b4b4' : isAdmin ? '#f59e0b' : '#f3f4f6';
  const color = isUser || isAdmin ? '#fff' : '#1f2937';
  const Icon = isUser ? UserIcon : isAdmin ? Shield : Bot;
  const label = isUser ? 'İstifadəçi' : isAdmin ? 'Admin' : 'AI köməkçisi';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start', marginBottom: 12 }}>
      <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
        <Icon size={11} /> {label} · {fmtDate(m.createdAt)}
      </div>
      <div style={{ background: bg, color, padding: '10px 14px', borderRadius: 12, maxWidth: '75%', fontSize: 14, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {m.content}
      </div>
    </div>
  );
}

export default function ManageChats() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/chats');
      setSessions(r.data);
      if (r.data.length && !activeId) setActiveId(r.data[0].id);
    } finally { setLoading(false); }
  };
  useEffect(() => { loadSessions(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDetail = async (id) => {
    if (!id) return;
    setDetailLoading(true);
    try {
      const r = await api.get(`/admin/chats/${id}`);
      setDetail(r.data);
    } finally { setDetailLoading(false); }
  };
  useEffect(() => { loadDetail(activeId); }, [activeId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [detail]);

  const sendReply = async () => {
    if (!reply.trim() || !activeId) return;
    setSending(true);
    try {
      await api.post(`/admin/chats/${activeId}/message`, { message: reply.trim() });
      setReply('');
      await loadDetail(activeId);
      await loadSessions();
    } finally { setSending(false); }
  };

  const removeSession = async (id) => {
    if (!window.confirm('Bu chat sessiyasını silməyə əminsiniz?')) return;
    await api.delete(`/admin/chats/${id}`);
    if (activeId === id) { setActiveId(null); setDetail(null); }
    await loadSessions();
  };

  return (
    <div>
      <h2 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 6px' }}>Chat sessiyaları</h2>
      <p style={{ color: '#6b7280', margin: '0 0 20px', fontSize: 14 }}>Ziyarətçilərin AI köməkçisi ilə söhbətləri. Lazım olsa cavab yazın — istifadəçiyə &laquo;Admin&raquo; olaraq görünəcək.</p>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center' }}><Loader2 className="spin" size={22} /></div>
      ) : sessions.length === 0 ? (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 40, textAlign: 'center', color: '#6b7280' }}>
          <MessageSquare size={32} style={{ margin: '0 auto 8px', display: 'block', opacity: 0.4 }} />
          Hələ chat sessiyası yoxdur
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 16, height: 'calc(100vh - 220px)', minHeight: 500 }}>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflowY: 'auto' }}>
            {sessions.map((s) => (
              <div key={s.id} onClick={() => setActiveId(s.id)} style={{
                padding: '12px 14px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer',
                background: s.id === activeId ? '#f0fdfa' : 'transparent',
                borderLeft: s.id === activeId ? '3px solid #17b4b4' : '3px solid transparent',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.visitor_name || `Ziyarətçi ${s.id.slice(0, 6)}`}
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); removeSession(s.id); }} style={{ background: 'transparent', border: 'none', color: '#dc2626', cursor: 'pointer' }} title="Sil">
                    <Trash2 size={13} />
                  </button>
                </div>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{s.message_count} mesaj · {fmtDate(s.lastActivity)}</div>
                {s.last_message && (
                  <div style={{ fontSize: 12, color: '#374151', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.last_message.role === 'user' ? '▸ ' : s.last_message.role === 'admin' ? '★ ' : '◆ '}{s.last_message.content}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, display: 'flex', flexDirection: 'column' }}>
            {detailLoading ? (
              <div style={{ padding: 40, textAlign: 'center' }}><Loader2 className="spin" size={22} /></div>
            ) : !detail ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Sessiya seçin</div>
            ) : (
              <>
                <div ref={scrollRef} style={{ flex: 1, padding: '20px 24px', overflowY: 'auto', background: '#fafafa' }}>
                  {detail.messages.map((m) => <Msg key={m.id} m={m} />)}
                </div>
                <div style={{ padding: 14, borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8 }}>
                  <input value={reply} onChange={(e) => setReply(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && sendReply()}
                    placeholder="Admin olaraq cavab yazın..." disabled={sending}
                    style={{ flex: 1, padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 999, outline: 'none', fontSize: 14 }} />
                  <button onClick={sendReply} disabled={sending || !reply.trim()} style={{ background: '#f59e0b', color: '#fff', border: 'none', padding: '0 20px', borderRadius: 999, cursor: sending ? 'wait' : 'pointer', fontWeight: 600, fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    {sending ? <Loader2 size={14} className="spin" /> : <Send size={14} />} Göndər
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
